import { useState, useRef, useEffect, useCallback } from 'react';
import { db } from '../../../../firebase';
import {
	doc,
	addDoc,
	setDoc,
	updateDoc,
	collection,
	query,
	where,
	getDocs,
	serverTimestamp,
} from 'firebase/firestore';
import { RECAP_TIERS } from '../recap/recapTiers';

/**
 * Lesson Recap drafts — the three per-(lesson × visibility) `documents` docs,
 * ported from LessonDashboard.js's triplicated save machinery into one hook.
 *
 * What carries over intact (crash-net behavior, not decoration):
 *  - 3s debounce autosave per tier, writing content + plainText + mentions.
 *  - 10s localStorage backup while dirty, restored via a 24h recovery banner.
 *    READS THE OLD PAGE'S KEY (`lessonDashboard_backup_${lessonId}`) so an
 *    in-flight backup survives the redesign cutover.
 *  - beforeunload guard while dirty.
 *
 * Stored visibility values are frozen ('student'/'parent'/'teacher'); display
 * names live in recapTiers.js only. Drafts save continuously — "finalize on
 * Complete" is just the last autosave; Complete never blocks on empty tiers.
 */
const useRecapDrafts = ({ lessonId, lesson, student, currentUser, authorFirstName = null }) => {
	// Per-tier state, keyed by stored visibility. Refs hold the live editor
	// payload ({json, text, mentionedStudentIds}) — state holds the LOADED
	// content only (TipTap is uncontrolled; re-setting content on keystroke
	// would fight the editor).
	const [tiers, setTiers] = useState(() =>
		Object.fromEntries(
			RECAP_TIERS.map((t) => [
				t.visibility,
				{ content: null, docId: null, saving: false, lastSaved: null },
			])
		)
	);
	const editorRefs = useRef(
		Object.fromEntries(
			RECAP_TIERS.map((t) => [t.visibility, { json: null, text: '', mentionedStudentIds: [] }])
		)
	);
	const docRefs = useRef(Object.fromEntries(RECAP_TIERS.map((t) => [t.visibility, null])));
	const saveTimers = useRef({});

	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [recoveryData, setRecoveryData] = useState(null);
	const backupTimer = useRef(null);

	const getBackupKey = useCallback(() => `lessonDashboard_backup_${lessonId}`, [lessonId]);

	const patchTier = useCallback((visibility, patch) => {
		setTiers((prev) => ({ ...prev, [visibility]: { ...prev[visibility], ...patch } }));
	}, []);

	// ---- localStorage crash net -------------------------------------------

	const saveToLocalStorage = useCallback(() => {
		if (!lessonId) return;
		try {
			localStorage.setItem(
				getBackupKey(),
				JSON.stringify({
					lessonId,
					timestamp: Date.now(),
					// Old-page field names, kept verbatim so backups round-trip in
					// BOTH directions across the cutover window.
					studentNote: editorRefs.current.student,
					parentNote: editorRefs.current.parent,
					teacherNote: editorRefs.current.teacher,
				})
			);
		} catch (error) {
			console.error('Error saving backup to localStorage:', error);
		}
	}, [lessonId, getBackupKey]);

	const clearLocalStorageBackup = useCallback(() => {
		try {
			localStorage.removeItem(getBackupKey());
		} catch (error) {
			console.error('Error clearing localStorage backup:', error);
		}
	}, [getBackupKey]);

	// Check for recovery data on mount — accepts backups written by the OLD page.
	useEffect(() => {
		if (!lessonId) return;
		try {
			const stored = localStorage.getItem(getBackupKey());
			if (!stored) return;
			const backup = JSON.parse(stored);
			const hoursSinceBackup = (Date.now() - backup.timestamp) / (1000 * 60 * 60);
			if (hoursSinceBackup < 24 && backup.lessonId === lessonId) {
				setRecoveryData(backup);
			} else {
				localStorage.removeItem(getBackupKey());
			}
		} catch (error) {
			console.error('Error checking for recovery data:', error);
		}
	}, [lessonId, getBackupKey]);

	const restoreFromBackup = useCallback(() => {
		if (!recoveryData) return;
		const mapping = { student: 'studentNote', parent: 'parentNote', teacher: 'teacherNote' };
		for (const [visibility, field] of Object.entries(mapping)) {
			const saved = recoveryData[field];
			if (saved && (saved.json || saved.text)) {
				editorRefs.current[visibility] = saved;
				patchTier(visibility, { content: saved.json || null });
			}
		}
		setRecoveryData(null);
		setHasUnsavedChanges(true);
	}, [recoveryData, patchTier]);

	const dismissRecovery = useCallback(() => {
		setRecoveryData(null);
		clearLocalStorageBackup();
	}, [clearLocalStorageBackup]);

	// Periodic backup while dirty.
	useEffect(() => {
		if (hasUnsavedChanges) {
			backupTimer.current = setInterval(saveToLocalStorage, 10000);
		}
		return () => {
			if (backupTimer.current) clearInterval(backupTimer.current);
		};
	}, [hasUnsavedChanges, saveToLocalStorage]);

	/**
	 * Flush on UNMOUNT — the in-app route change that `beforeunload` cannot see.
	 *
	 * `beforeunload` fires for a tab close or a reload. It does NOT fire when
	 * React Router swaps the route, so navigating out of a lesson could drop up
	 * to 10 seconds of recap typing (the periodic backup's interval) with no
	 * warning and no recovery banner to find afterwards. Found while making
	 * "leave a lesson" non-destructive, 2026-09-02.
	 *
	 * Reads the flags through refs so the cleanup runs exactly once, on unmount,
	 * rather than on every change of `hasUnsavedChanges` — an effect that
	 * re-subscribes would write a backup on each keystroke's state change.
	 */
	const dirtyRef = useRef(hasUnsavedChanges);
	const saveRef = useRef(saveToLocalStorage);
	dirtyRef.current = hasUnsavedChanges;
	saveRef.current = saveToLocalStorage;
	useEffect(
		() => () => {
			if (dirtyRef.current) saveRef.current();
		},
		[]
	);

	// beforeunload guard while dirty (one final backup on the way out).
	useEffect(() => {
		const handleBeforeUnload = (e) => {
			if (hasUnsavedChanges) {
				saveToLocalStorage();
				e.preventDefault();
				e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
				return e.returnValue;
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [hasUnsavedChanges, saveToLocalStorage]);

	// ---- Firestore load + save --------------------------------------------

	// Load existing recap docs for this lesson, bucketed by stored visibility.
	useEffect(() => {
		if (!lessonId) return;
		const loadNotes = async () => {
			try {
				const notesQuery = query(collection(db, 'documents'), where('lessonId', '==', lessonId));
				const snapshot = await getDocs(notesQuery);
				snapshot.docs.forEach((noteDoc) => {
					const data = noteDoc.data();
					const visibility = data.visibility || 'teacher'; // default most restrictive
					if (!(visibility in docRefs.current)) return;
					docRefs.current[visibility] = doc(db, 'documents', noteDoc.id);
					// `lastSaved` seeded from the stored doc so the topbar's save
					// state can read "Saved Sep 18" on a reopened lesson instead of
					// "Not started" over a written recap (design pass 2, change 5).
					const storedAt = data.updatedAt?.toDate ? data.updatedAt.toDate() : null;
					patchTier(visibility, { docId: noteDoc.id, content: data.content || null, lastSaved: storedAt });

					// SEED editorRefs FROM THE SAVED DOC, not just the tiers state.
					// `tierHasContent` (and therefore the wrap-up's "Send without a
					// parent recap?" confirm) reads editorRefs, which is written ONLY
					// by handleTierUpdate — i.e. only when someone types. Reopening a
					// lesson that already HAS recaps left the ref empty, so a fully
					// written parent recap reported as absent and the sheet warned
					// about sending without one while displaying it. Reported live
					// 2026-08-05.
					//
					// Guarded: the load is async and can land after the teacher has
					// already started typing, and the typed value must win. Seeding
					// touches a ref only, so it cannot mark the draft dirty.
					const alreadyTyped = editorRefs.current[visibility]?.text?.trim();
					if (!alreadyTyped) {
						editorRefs.current[visibility] = {
							json: data.content || null,
							text: data.plainText || '',
							mentionedStudentIds: data.studentIds || [],
						};
					}
				});
			} catch (error) {
				console.error('Error loading recap documents:', error);
			}
		};
		loadNotes();
	}, [lessonId, patchTier]);

	const saveTier = useCallback(
		async (visibility) => {
			if (!currentUser || !lessonId) return;
			patchTier(visibility, { saving: true });

			const editor = editorRefs.current[visibility];
			const studentId = lesson?.studentId || null;
			const mentionedIds = editor?.mentionedStudentIds || [];
			const allStudentIds = studentId ? [...new Set([studentId, ...mentionedIds])] : mentionedIds;

			// Old-page title labels, kept for continuity in the documents library.
			const visibilityLabels = {
				student: 'Student & Parent',
				parent: 'Parent Only',
				teacher: 'Teacher Only',
			};

			const noteData = {
				content: editor?.json || null,
				plainText: editor?.text || '',
				// TWO STUDENT FIELDS, and the difference is now load-bearing.
				// `studentId` is the scalar owner of the recap — this lesson's own
				// student — and it is what the customer read rule keys on to resolve
				// the family (via students/{id}.familyId). `studentIds` stays the
				// MENTION INDEX: it can name children of other families, a rule
				// cannot iterate an array, and being mentioned grants nothing.
				// Null when a lesson has no student; the customer branch then never
				// matches, which is correct.
				studentId,
				studentIds: allStudentIds,
				lessonId,
				type: 'lesson_note',
				visibility,
				updatedAt: serverTimestamp(),
				updatedBy: currentUser.uid,
			};

			try {
				if (docRefs.current[visibility]) {
					await setDoc(docRefs.current[visibility], noteData, { merge: true });
				} else {
					const studentName = student?.fullName || lesson?.studentName || 'Unknown';
					const today = new Date().toLocaleDateString('en-US', {
						month: 'short',
						day: 'numeric',
						year: 'numeric',
					});
					const docRef = await addDoc(collection(db, 'documents'), {
						...noteData,
						title: `${studentName} - ${today} (${visibilityLabels[visibility]})`,
						createdAt: serverTimestamp(),
						createdBy: currentUser.uid,
						// What a FAMILY can show as "with Annelise": a customer cannot
						// read `users` or `teachers`, so createdBy resolves to nothing on
						// their side. First name only, set once by whoever opened the
						// recap. Null when unknown; the family surface then omits it.
						authorFirstName: authorFirstName || null,
						tags: [],
						archived: false,
						pinned: false,
					});
					docRefs.current[visibility] = docRef;
					patchTier(visibility, { docId: docRef.id });
				}
				patchTier(visibility, { lastSaved: new Date() });
				setHasUnsavedChanges(false);
				clearLocalStorageBackup();
			} catch (error) {
				console.error(`Error saving ${visibility} recap:`, error);
			} finally {
				patchTier(visibility, { saving: false });
			}
		},
		[currentUser, lessonId, lesson, student, patchTier, clearLocalStorageBackup, authorFirstName]
	);

	/** Editor onUpdate handler for a tier — 3s debounce, dirty-flagging. */
	const handleTierUpdate = useCallback(
		(visibility, editorContent) => {
			editorRefs.current[visibility] = editorContent;
			setHasUnsavedChanges(true);
			if (saveTimers.current[visibility]) clearTimeout(saveTimers.current[visibility]);
			saveTimers.current[visibility] = setTimeout(() => saveTier(visibility), 3000);
		},
		[saveTier]
	);

	/** Flush every dirty tier NOW (wrap-up's Complete calls this before closing). */
	const flushAll = useCallback(async () => {
		for (const t of RECAP_TIERS) {
			if (saveTimers.current[t.visibility]) {
				clearTimeout(saveTimers.current[t.visibility]);
				saveTimers.current[t.visibility] = null;
			}
		}
		if (!hasUnsavedChanges) return;
		await Promise.all(RECAP_TIERS.map((t) => saveTier(t.visibility)));
	}, [hasUnsavedChanges, saveTier]);

	/** Which tiers currently hold any text — the wrap-up's empty-tier confirm. */
	const tierHasContent = useCallback(
		(visibility) => Boolean(editorRefs.current[visibility]?.text?.trim()),
		[]
	);

	/** Plain text of a tier — the recap email's source (family-visible tiers only). */
	const tierText = useCallback(
		(visibility) => editorRefs.current[visibility]?.text || '',
		[]
	);

	/**
	 * The LIVE content for a tier — what the teacher has actually typed.
	 *
	 * READ THIS, NOT `tiers[visibility].content`, when mounting a second editor
	 * for the same tier. Bug found in production 2026-08-05: `handleTierUpdate`
	 * writes only to `editorRefs` (a ref, deliberately — patching state on every
	 * keystroke would re-render the editor mid-typing), so `tiers[].content` is
	 * set ONLY on load from Firestore and on restore-from-backup. It is stale the
	 * instant anyone types.
	 *
	 * The Wrap-up sheet mounts a SECOND `RecapTierEditor` per tier, over the top
	 * of the right column's. It read `tiers[].content`, got the value loaded at
	 * mount (null on a fresh lesson), and rendered EMPTY editors while the
	 * teacher's text sat in the right-hand editor behind it — so "Complete
	 * lesson" appeared to have thrown the recap away. It had not; TipTap's
	 * `useEditor` takes `content` as INITIAL content only, so the two editors
	 * simply started from different values.
	 *
	 * `tiers[]` is kept as the fallback for the pre-typing case (it carries what
	 * was loaded from the saved doc) and because a restore-from-backup patches
	 * both.
	 */
	const liveTierContent = useCallback(
		(visibility) => editorRefs.current[visibility]?.json ?? tiers[visibility]?.content ?? null,
		[tiers]
	);

	/**
	 * Stamp `studentId` onto recaps that were saved while this lesson had no
	 * student — the repair half of the "linking later does NOT fix the recaps"
	 * defect (found in Anthony's live QA 2026-08-05).
	 *
	 * `saveTier` resolves the owner as `lesson?.studentId || null` at SAVE time,
	 * and nothing back-filled it afterwards. So linking a student repaired the
	 * lesson doc and left its recaps at `studentId: null` — which the customer
	 * `documents` rule rejects (`get('studentId', null) != null`), permanently
	 * and silently. The teacher had every reason to believe the family could
	 * read them.
	 *
	 * Two properties that are load-bearing rather than defensive:
	 *
	 *  - It reads from Firestore rather than `docRefs`, because a recap written
	 *    in an earlier session is only in `docRefs` if the load effect has
	 *    already landed, and this runs right after a link.
	 *  - IT ONLY EVER FILLS AN EMPTY OWNER. A doc that already names a student
	 *    is left untouched, because overwriting one would show a family another
	 *    family's lesson feedback — the single worst outcome this whole field
	 *    exists to prevent. Re-linking a lesson to a different student
	 *    therefore does NOT re-point its existing recaps; that is deliberate,
	 *    and the correct fix there is to delete the wrong recap, not to
	 *    silently re-address it.
	 *
	 * `studentIds` (the mention index) gains the owner too, matching what
	 * `saveTier` writes on a normal save so the two paths cannot diverge.
	 */
	const backfillStudentId = useCallback(
		async (newStudentId) => {
			if (!lessonId || !newStudentId) return 0;
			try {
				const snapshot = await getDocs(
					query(collection(db, 'documents'), where('lessonId', '==', lessonId))
				);
				const orphans = snapshot.docs.filter((d) => !d.data().studentId);
				await Promise.all(
					orphans.map((d) => {
						const mentioned = d.data().studentIds || [];
						return updateDoc(doc(db, 'documents', d.id), {
							studentId: newStudentId,
							studentIds: [...new Set([newStudentId, ...mentioned])],
							updatedAt: serverTimestamp(),
						});
					})
				);
				return orphans.length;
			} catch (error) {
				console.error('Error back-filling recap studentId:', error);
				return 0;
			}
		},
		[lessonId]
	);

	// Clear debounce timers on unmount (backups + beforeunload cover the rest).
	useEffect(() => {
		const timers = saveTimers.current;
		return () => {
			Object.values(timers).forEach((t) => t && clearTimeout(t));
		};
	}, []);

	return {
		tiers,
		handleTierUpdate,
		flushAll,
		tierHasContent,
		tierText,
		liveTierContent,
		backfillStudentId,
		// No student on the lesson means every recap saved here is written with
		// `studentId: null` and can never be read by a family. Surfaced so the
		// tier banners can say so INSTEAD of promising an audience that cannot
		// see it — the banner's whole job is stating who reads a tier, and on an
		// unlinked lesson the family-visible ones were stating it falsely.
		noStudentLinked: !lesson?.studentId,
		hasUnsavedChanges,
		recoveryAvailable: Boolean(recoveryData),
		restoreFromBackup,
		dismissRecovery,
	};
};

export default useRecapDrafts;
